/**
 * Login screen shared by admin and webmail (same task, same UI). The password is checked by
 * the platform API against grommunio; this component only collects it.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useI18n } from '@platform/i18n';
import { AlertCircleIcon, InfoIcon, LogInIcon, MailIcon } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { IdentityClient } from '@platform/api-client';
import { MenuSelect } from '../components/menu-select';
import { Label } from '../components/label';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../components/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/form';
import { Input } from '../components/input';
import { Alert } from '../components/misc';
import { BrandLogo, useBranding } from './branding';
import { DisplayMenu } from './preference-controls';
import { VersionFooter } from './version';

export interface LoginScreenProps {
  identity?: IdentityClient;
  onFederationDone?: () => void;
  onFederationMfa?: () => void;
  children?: ReactNode;
  /** Defaults to the instance name (Settings → Branding). */
  productName?: string;
  subtitle: string;
  /** Icon shown when no logo is uploaded. */
  logo?: ReactNode;
  /** Resolves on success; rejects (any error) on wrong credentials. */
  onLogin: (credentials: { username: string; password: string }) => Promise<void>;
  /** Optional info note (e.g. preview mode). */
  hint?: string;
  /** Message for a failed login (default: wrong username or password). */
  describeError?: (error: unknown) => string;
  /** A failed app-admission check, kept distinct from a submitted password failure. */
  admissionError?: string;
  onRetryAdmission?: () => void;
}

export function LoginScreen({ productName, subtitle, logo, onLogin, hint, describeError, admissionError, onRetryAdmission, children, identity, onFederationDone, onFederationMfa }: LoginScreenProps) {
  const { t } = useI18n();
  const { instanceName, logoUrl, backgroundUrl } = useBranding();
  const [failure, setFailure] = useState<string>();
  const [providers, setProviders] = useState<{ id: string; name: string }[]>([]);
  const [providerId, setProviderId] = useState('');
  const [unlock, setUnlock] = useState<string>();
  const [nativePassword, setNativePassword] = useState('');
  const [identityBusy, setIdentityBusy] = useState(false);
  useEffect(() => {
    let active = true;
    void identity?.providers().then(value => { if (active) { setProviders(value.providers); setProviderId(value.providers[0]?.id ?? ''); } }, () => undefined);
    void identity?.pending().then(value => { if (active && value.pending) setUnlock(value.address || t('identity.directoryAccount')); }, () => undefined);
    return () => { active = false; };
  }, [identity, t]);
  const federationError = (error: unknown) => {
    if (typeof error === 'object' && error !== null && 'code' in error && error.code === 'mfa_required') { setUnlock(undefined); onFederationMfa?.(); }
    else { setFailure(t('identity.failed')); setUnlock(undefined); }
  };
  const completeFederation = async () => {
    setIdentityBusy(true); setFailure(undefined);
    try { await identity?.complete(nativePassword || undefined); onFederationDone?.(); }
    catch (error) { federationError(error); }
    finally { setNativePassword(''); setIdentityBusy(false); }
  };
  const schema = useMemo(
    () =>
      z.object({
        username: z.string().trim().min(1, t('auth.usernameRequired')),
        password: z.string().min(1, t('auth.passwordRequired')),
      }),
    [t],
  );
  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { username: '', password: '' } });

  const submit = form.handleSubmit(async (values) => {
    setFailure(undefined);
    try {
      await onLogin(values);
    } catch (error) {
      setFailure(describeError?.(error) ?? t('auth.invalidCredentials'));
      form.resetField('password');
    }
  });

  return (
    <main className="relative flex min-h-dvh items-center justify-center bg-muted/40 px-4 py-12">
      {backgroundUrl && <img src={backgroundUrl} alt="" aria-hidden className="absolute inset-0 size-full object-cover" />}
      <div className="absolute top-2 right-2 rounded-md bg-background/80">
        <DisplayMenu />
      </div>
      <Card className="relative w-full max-w-sm gap-4 py-4">
        <CardHeader className="items-center gap-2 px-6 text-center">
          {logoUrl ? (
            <BrandLogo size="card" fallback={null} />
          ) : (
            <span className="flex size-10 items-center justify-center rounded-lg bg-primary text-primary-foreground [&_svg]:size-5">
              {logo ?? <MailIcon />}
            </span>
          )}
          <CardTitle className="text-lg">{productName ?? instanceName}</CardTitle>
          <CardDescription>{subtitle}</CardDescription>
        </CardHeader>
        <CardContent className="px-6">
          {children ?? (unlock ? <form className="flex flex-col gap-4" onSubmit={event => { event.preventDefault(); void completeFederation(); }}>
            <p className="text-sm">{t('identity.unlockHint', { address: unlock })}</p>
            {failure && <Alert variant="destructive">{failure}</Alert>}
            <Label htmlFor="federation-password">{t('identity.nativePassword')}</Label>
            <Input id="federation-password" type="password" autoComplete="current-password" value={nativePassword} onChange={event => setNativePassword(event.target.value)} />
            <Button type="submit" disabled={identityBusy}>{t('identity.finish')}</Button>
            <Button type="button" variant="outline" disabled={identityBusy} onClick={() => setUnlock(undefined)}>{t('identity.localLogin')}</Button>
          </form> : <>
          <Form {...form}>
            <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4" noValidate>
              {(failure || admissionError) && (
                <Alert variant="destructive">
                  <AlertCircleIcon />
                  <div>{failure ?? admissionError}
                    {admissionError && onRetryAdmission && <Button type="button" variant="outline" size="sm" className="mt-2" disabled={form.formState.isSubmitting} onClick={onRetryAdmission}>{t('common.retry')}</Button>}
                  </div>
                </Alert>
              )}
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.username')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="username" autoFocus placeholder={t('auth.usernamePlaceholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.password')}</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="current-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={form.formState.isSubmitting} className="w-full">
                <LogInIcon />
                {form.formState.isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
              </Button>
              {hint && (
                <p className="flex items-start gap-2 text-xs text-muted-foreground">
                  <InfoIcon className="mt-0.5 size-3.5 shrink-0" />
                  {hint}
                </p>
              )}
            </form>
          </Form>
          {providers.length > 0 && <div className="mt-4 flex flex-col gap-2">
            <MenuSelect label={t('identity.provider')} value={providerId} options={providers.map(value => ({ value: value.id, label: value.name }))} onChange={setProviderId} />
            <Button variant="outline" disabled={identityBusy || !providerId} onClick={() => {
              setIdentityBusy(true); void identity?.start(providerId).then(value => { window.location.assign(value.url); }, error => { federationError(error); setIdentityBusy(false); });
            }}>{t('identity.continue')}</Button>
          </div>}
          </>)}
        </CardContent>
      </Card>
      <VersionFooter />
    </main>
  );
}
